import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './components/LanguageSwitcher';
import { getShopProducts } from './i18n/shopProducts';
import traceMopIcon from './tracemop-icon.png';

const TEAL = '#14b8a6';
const TEAL_DIM = '#0F766E';
const TEAL_GLOW = 'rgba(20,184,166,0.13)';
const TEAL_BORDER = 'rgba(20,184,166,0.22)';
const BG = '#0F172A';
const BG_ALT = '#0a0f1a';
const BG_CARD = '#1e293b';
const WHITE = '#f8fafc';
const MUTED = '#94a3b8';

function NavLink({ href, children, onClick }) {
  return (
    <a
      href={href}
      onClick={onClick}
      style={{ color: MUTED, fontSize: 11, fontWeight: 500, textDecoration: 'none', cursor: 'pointer' }}
      onMouseEnter={e => e.target.style.color = WHITE}
      onMouseLeave={e => e.target.style.color = MUTED}
    >
      {children}
    </a>
  );
}

function Field({ label, value, set, type = 'text', placeholder, required = true }) {
  return (
    <div>
      <label style={{ fontSize: 12, color: MUTED, fontWeight: 500, display: 'block', marginBottom: 6 }}>{label}</label>
      <input
        required={required} type={type} value={value}
        onChange={e => set(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', background: BG, border: `1px solid ${TEAL_BORDER}`,
          borderRadius: 6, padding: '10px 14px',
          fontSize: 14, color: WHITE, fontFamily: 'inherit', outline: 'none',
        }}
      />
    </div>
  );
}

function StarterOrderModal({ onClose }) {
  const { t } = useTranslation();
  const STEPS = t('shop.modal.steps', { returnObjects: true });
  const starterIncludes = t('shop.modal.starterIncludes', { returnObjects: true });
  const terms = t('shop.modal.terms', { returnObjects: true });
  const starterProduct = t('shop.products.starter', { returnObjects: true });
  const [step, setStep] = useState(1);
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState({ name: '', org: '', email: '', phone: '', address: '' });
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '', name: '' });
  const [done, setDone] = useState(false);

  const setF = k => v => setForm(f => ({ ...f, [k]: v }));
  const setC = k => v => setCard(c => ({ ...c, [k]: v }));

  const canNext = () => {
    if (step === 1) return agreed;
    if (step === 2) return form.name && form.org && form.email;
    if (step === 3) return card.number && card.expiry && card.cvc && card.name;
    return true;
  };

  const handleNext = () => {
    if (step < 3) setStep(s => s + 1);
    else { setDone(true); setTimeout(onClose, 3000); }
  };

  const inputStyle = {
    width: '100%', background: BG, border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 6, padding: '10px 14px',
    fontSize: 14, color: WHITE, fontFamily: 'inherit', outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: BG_CARD, borderRadius: 20, width: '100%', maxWidth: 540,
        border: `1px solid ${TEAL_BORDER}`,
        boxShadow: `0 0 80px rgba(20,184,166,0.14)`,
        overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '24px 32px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: TEAL, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 4 }}>
                {starterProduct.name}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: WHITE }}>
                {done ? t('shop.modal.orderConfirmed') : STEPS[step]}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          {/* Step indicators */}
          {!done && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
              {STEPS.map((s, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    height: 3, width: '100%', borderRadius: 2,
                    background: i <= step ? TEAL : 'rgba(255,255,255,0.08)',
                    transition: 'background 0.3s',
                  }} />
                  <div style={{ fontSize: 10, color: i <= step ? TEAL : '#475569', fontWeight: 600 }}>{s}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '0 32px 28px', maxHeight: '55vh', overflowY: 'auto' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEAL, marginBottom: 10 }}>{t('shop.modal.orderReceived')}</div>
              <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.7 }}>
                {t('shop.modal.orderReceivedBody')}
              </div>
            </div>
          ) : step === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {[
                { icon: '📡', ...starterIncludes[0] },
                { icon: '🗺️', ...starterIncludes[1] },
                { icon: '📊', ...starterIncludes[2] },
              ].map(({ icon, title, sub, desc }) => (
                <div key={title} style={{
                  background: 'rgba(20,184,166,0.05)', border: `1px solid ${TEAL_BORDER}`,
                  borderRadius: 10, padding: '16px 18px',
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: WHITE, marginBottom: sub ? 2 : 6 }}>{title}</div>
                      {sub && <div style={{ fontSize: 11, color: TEAL, fontWeight: 600, marginBottom: 6 }}>{sub}</div>}
                      <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.65 }}>{desc}</div>
                    </div>
                  </div>
                </div>
              ))}
              <div
                style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: MUTED, textAlign: 'center' }}
                dangerouslySetInnerHTML={{ __html: t('shop.modal.starterPriceNote') }}
              />
            </div>
          ) : step === 1 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {terms.map(({ title, body }) => (
                <div key={title} style={{ borderLeft: `3px solid ${TEAL_BORDER}`, paddingLeft: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: WHITE, marginBottom: 5 }}>{title}</div>
                  <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.7 }}>{body}</div>
                </div>
              ))}
              <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                  style={{ marginTop: 3, accentColor: TEAL, width: 16, height: 16, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  {t('shop.modal.termsAgree')}
                </span>
              </label>
            </div>
          ) : step === 2 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label={t('shop.modal.fields.fullName')} value={form.name} set={setF('name')} placeholder={t('shop.modal.placeholders.fullName')} />
              <Field label={t('shop.modal.fields.organisation')} value={form.org} set={setF('org')} placeholder={t('shop.modal.placeholders.organisation')} />
              <Field label={t('shop.modal.fields.workEmail')} value={form.email} set={setF('email')} type="email" placeholder={t('shop.modal.placeholders.email')} />
              <Field label={t('shop.modal.fields.phone')} value={form.phone} set={setF('phone')} type="tel" placeholder={t('shop.modal.placeholders.phone')} required={false} />
              <Field label={t('shop.modal.fields.billingAddress')} value={form.address} set={setF('address')} placeholder={t('shop.modal.placeholders.address')} required={false} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: 'rgba(20,184,166,0.06)', border: `1px solid ${TEAL_BORDER}`, borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>{t('shop.modal.orderSummary')}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: WHITE, marginBottom: 6 }}>
                  <span>{t('shop.modal.firstMonth')}</span><strong>€60</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: MUTED, marginBottom: 2 }}>
                  <span>{t('shop.modal.securityHold')}</span><span>€700</span>
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                  {t('shop.modal.holdRelease')}
                </div>
                <div style={{ borderTop: `1px solid rgba(255,255,255,0.07)`, marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: WHITE }}>
                  <span>{t('shop.modal.chargedToday')}</span><span>€60</span>
                </div>
              </div>

              <Field label={t('shop.modal.fields.cardholderName')} value={card.name} set={setC('name')} placeholder={t('shop.modal.placeholders.fullName')} />
              <Field label={t('shop.modal.fields.cardNumber')} value={card.number} set={setC('number')} placeholder={t('shop.modal.placeholders.cardNumber')} />
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}><Field label={t('shop.modal.fields.expiry')} value={card.expiry} set={setC('expiry')} placeholder={t('shop.modal.placeholders.expiry')} /></div>
                <div style={{ flex: 1 }}><Field label={t('shop.modal.fields.cvc')} value={card.cvc} set={setC('cvc')} placeholder={t('shop.modal.placeholders.cvc')} /></div>
              </div>
              <div style={{ fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                {t('shop.modal.paymentSecure')}
              </div>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        {!done && (
          <div style={{
            padding: '16px 32px 24px',
            borderTop: `1px solid rgba(255,255,255,0.05)`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          }}>
            {step > 0 ? (
              <button onClick={() => setStep(s => s - 1)} style={{
                background: 'transparent', border: `1px solid rgba(255,255,255,0.1)`,
                borderRadius: 8, padding: '11px 24px', fontSize: 14, color: MUTED,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>{t('shop.modal.back')}</button>
            ) : (
              <button onClick={onClose} style={{
                background: 'transparent', border: 'none',
                fontSize: 13, color: '#475569', cursor: 'pointer', fontFamily: 'inherit',
              }}>{t('shop.modal.cancel')}</button>
            )}
            <button
              onClick={handleNext}
              disabled={!canNext()}
              style={{
                background: canNext() ? TEAL : '#1e293b',
                border: 'none', borderRadius: 8, padding: '11px 28px',
                fontSize: 14, fontWeight: 700, color: canNext() ? '#fff' : '#475569',
                cursor: canNext() ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
            >
              {step === 3 ? t('shop.modal.placeOrder') : step === 1 ? t('shop.modal.agreeContinue') : t('shop.modal.continue')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderModal({ product, onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(onClose, 2400);
  };

  const inputStyle = {
    width: '100%', background: BG, border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 6, padding: '10px 14px',
    fontSize: 14, color: WHITE, fontFamily: 'inherit', outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: BG_CARD, borderRadius: 16, padding: '36px 40px',
        maxWidth: 460, width: '100%',
        border: `1px solid ${TEAL_BORDER}`,
        boxShadow: `0 0 60px rgba(20,184,166,0.12)`,
      }} onClick={e => e.stopPropagation()}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEAL, marginBottom: 8 }}>{t('shop.modal.requestReceived')}</div>
            <div style={{ fontSize: 14, color: MUTED }}>{t('shop.modal.requestReceivedBody')}</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 8 }}>{product.cta}</div>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: WHITE, marginBottom: 4 }}>{product.name}</h3>
            <div style={{ fontSize: 13, color: MUTED, marginBottom: 28 }}>{product.coverage}</div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: t('shop.modal.fields.yourName'), value: name, set: setName, type: 'text', placeholder: t('shop.modal.placeholders.yourName') },
                { label: t('shop.modal.fields.workEmail'), value: email, set: setEmail, type: 'email', placeholder: t('shop.modal.placeholders.email') },
                { label: t('shop.modal.fields.organisation'), value: org, set: setOrg, type: 'text', placeholder: t('shop.modal.placeholders.organisation') },
              ].map(({ label, value, set, type, placeholder }) => (
                <div key={label}>
                  <label style={{ fontSize: 12, color: MUTED, fontWeight: 500, display: 'block', marginBottom: 6 }}>{label}</label>
                  <input required type={type} value={value} onChange={e => set(e.target.value)} placeholder={placeholder} style={inputStyle} />
                </div>
              ))}
              <button type="submit" style={{ marginTop: 8, background: TEAL, border: 'none', borderRadius: 8, padding: '13px 0', width: '100%', fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                {t('shop.modal.sendRequest')}
              </button>
            </form>
            <button onClick={onClose} style={{ marginTop: 14, background: 'none', border: 'none', fontSize: 12, color: '#475569', cursor: 'pointer', width: '100%', fontFamily: 'inherit' }}>{t('shop.modal.cancel')}</button>
          </>
        )}
      </div>
    </div>
  );
}

function ProductCard({ product, onOrder }) {
  const isPrimary = product.ctaStyle === 'primary';
  return (
    <div style={{
      background: BG_CARD,
      border: isPrimary ? `1.5px solid ${TEAL}` : `1px solid rgba(255,255,255,0.07)`,
      borderRadius: 16,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: isPrimary ? `0 0 40px rgba(20,184,166,0.10)` : 'none',
      flex: 1,
      minWidth: 280,
      maxWidth: 380,
    }}>
      {/* Badge */}
      <div style={{
        position: 'absolute', top: 16, right: 16,
        background: product.badgeColor,
        color: '#fff', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.8px', textTransform: 'uppercase',
        padding: '4px 12px', borderRadius: 999,
      }}>
        {product.badge}
      </div>

      {/* Product image */}
      <div style={{
        background: product.imgBg,
        height: 220,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
        <img
          src={product.img}
          alt={product.name}
          style={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain' }}
        />
      </div>

      {/* Content */}
      <div style={{ padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 8 }}>
          {product.coverage}
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: WHITE, marginBottom: 8, letterSpacing: '-0.5px' }}>
          {product.name}
        </h3>
        <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 20 }}>
          {product.tagline}
        </p>

        {/* Features */}
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 28, flex: 1 }}>
          {product.features.map(f => (
            <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: MUTED }}>
              <span style={{ color: TEAL, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* Price */}
        <div style={{ borderTop: `1px solid rgba(255,255,255,0.06)`, paddingTop: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: product.period ? 32 : 22, fontWeight: 800, color: WHITE }}>
              {product.price}
            </span>
            {product.period && (
              <span style={{ fontSize: 14, color: MUTED }}>{product.period}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>{product.note}</div>
        </div>

        {/* CTA */}
        <button
          onClick={() => onOrder(product)}
          style={{
            width: '100%', padding: '13px 0',
            background: isPrimary ? TEAL : 'transparent',
            border: isPrimary ? 'none' : `1px solid ${TEAL_BORDER}`,
            borderRadius: 8,
            fontSize: 14, fontWeight: 700,
            color: isPrimary ? '#fff' : TEAL,
            cursor: 'pointer', fontFamily: 'inherit',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.target.style.background = isPrimary ? '#0d9488' : TEAL_GLOW; }}
          onMouseLeave={e => { e.target.style.background = isPrimary ? TEAL : 'transparent'; }}
        >
          {product.cta}
        </button>
      </div>
    </div>
  );
}

const WebshopPage = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const products = getShopProducts(t);
  const [activeModal, setActiveModal] = useState(null);
  const [showStarterModal, setShowStarterModal] = useState(false);
  const starterProduct = products.find((p) => p.id === 'starter');
  const accessoryProducts = products.filter((p) => p.id !== 'starter');

  const handleOrder = (product) => {
    if (product.id === 'starter') setShowStarterModal(true);
    else setActiveModal(product);
  };

  const handleOpenApp = () => navigate('/app');
  const handleLogout = async () => {
    await fetch('/auth/logout', { credentials: 'include' });
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", WebkitFontSmoothing: 'antialiased', background: BG, color: WHITE, minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input:focus { border-color: ${TEAL} !important; outline: none; }
        input::placeholder { color: #334155; }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 38px', height: 48,
        background: 'rgba(15,23,42,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src={traceMopIcon} alt="Trace Mop" style={{ height: 29, width: 29, filter: 'invert(1) hue-rotate(180deg) saturate(1.4)' }} />
          <span style={{ fontSize: 14, fontWeight: 800, color: WHITE, letterSpacing: '-0.3px' }}>Trace Mop</span>
        </a>

        <div style={{ display: 'flex', gap: 26, alignItems: 'center' }}>
          <NavLink href="/#problem">{t('common.nav.problem')}</NavLink>
          <NavLink href="/#video">{t('common.nav.video')}</NavLink>
          <NavLink href="/#how-it-works">{t('common.nav.howItWorks')}</NavLink>
          <NavLink href="/#dashboard">{t('common.nav.dashboard')}</NavLink>
          <NavLink href="/#audience">{t('common.nav.audience')}</NavLink>
          <span style={{ fontSize: 11, fontWeight: 700, color: WHITE }}>{t('common.nav.shop')}</span>
        </div>

        <div className="flex items-center gap-2.5 pr-3">
          <LanguageSwitcher scale={0.8} />
          <button
            className="app-btn text-[10px] font-semibold px-3 py-1.5 rounded border border-teal-500 text-teal-400 hover:bg-teal-500 hover:text-white transition"
            onClick={handleOpenApp}
          >
            {t('common.appButton')}
          </button>
          <div
            className="w-[29px] h-[29px] rounded-full bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center cursor-pointer"
            onClick={handleLogout}
            title={t('common.signOutTitle', { name: user?.name || user?.email })}
          >
            {user?.photo_url ? (
              <img src={user.photo_url} alt="avatar" className="w-full h-full object-cover" draggable={false} />
            ) : user?.picture ? (
              <img src={user.picture} alt="avatar" className="w-full h-full object-cover" draggable={false} />
            ) : (
              <span className="text-[10px] font-semibold text-slate-100">
                {(user?.name || user?.email || 'U').trim()[0]?.toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </nav>

      {/* ── HEADER ── */}
      <section style={{ textAlign: 'center', padding: '80px 48px 60px', background: BG_ALT }}>
        <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 14 }}>{t('shop.header.label')}</p>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 800, letterSpacing: '-2px', color: WHITE, marginBottom: 18 }}>
          {t('shop.header.title')}
        </h1>
        <p style={{ fontSize: 16, color: MUTED, maxWidth: 540, margin: '0 auto', lineHeight: 1.75 }}>
          {t('shop.header.subtitle')}
        </p>
      </section>

      {/* ── PRODUCT CARDS ── */}
      <section style={{ padding: '60px 48px 100px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <p style={{ fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 8 }}>
            {t('shop.sections.starter')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {starterProduct ? <ProductCard key={starterProduct.id} product={starterProduct} onOrder={handleOrder} /> : null}
        </div>

        <div style={{ textAlign: 'center', marginTop: 46, marginBottom: 24 }}>
          <p style={{ fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 8 }}>
            {t('shop.sections.accessories')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {accessoryProducts.map(p => (
            <ProductCard key={p.id} product={p} onOrder={handleOrder} />
          ))}
        </div>

        {/* Money-back note */}
        <div style={{
          textAlign: 'center', marginTop: 52,
          padding: '24px 32px',
          background: BG_CARD,
          borderRadius: 12,
          border: `1px solid rgba(255,255,255,0.06)`,
          maxWidth: 640, margin: '52px auto 0',
        }}>
          <div style={{ fontSize: 22, marginBottom: 10 }}>🛡️</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: WHITE, marginBottom: 8 }}>{t('shop.guarantee.title')}</div>
          <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.7 }}>
            {t('shop.guarantee.body')}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#07090f', padding: '28px 80px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={traceMopIcon} alt="" style={{ height: 24, width: 24, filter: 'invert(1) hue-rotate(180deg) saturate(1.4)' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: WHITE }}>Trace Mop</span>
          </div>
          <span style={{ fontSize: 12, color: '#475569' }}>{t('common.footer.copyright')}</span>
          <span style={{ fontSize: 12, color: '#475569' }}>{t('common.footer.domain')}</span>
        </div>
      </footer>

      {/* ── ORDER MODAL ── */}
      {showStarterModal && <StarterOrderModal onClose={() => setShowStarterModal(false)} />}
      {activeModal && <OrderModal product={activeModal} onClose={() => setActiveModal(null)} />}
    </div>
  );
};

export default WebshopPage;
